import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import FilterBar from "components/common/FilterBar";
import { useTranslation } from "react-i18next";
import { getMinMaxDate } from "utils/utils";
import { getActionFlags } from "utils/getActionFlags";
import { Link, useNavigate } from "react-router-dom";
import { Icons } from "assets/icons";
import { showDeleteModal } from "store/slices/modalSlice";
import Switch from "components/common/Switch";
import { usePermission } from "hooks/usePermission";
import TableShimmer from "components/common/TableShimmer";
import NoDataFound from "components/common/NoDataFound";
import { ORDER_BY_OPTIONS, STATUS_OPTIONS } from "utils/constants";
import { handleStatusUpdate } from "helpers/apiUtils";
import Paginations from "components/common/Paginations";
import {
  getBranches,
  resetFilters,
  setFilter,
  updateBranchStatus,
} from "store/slices/branchesSlice";
import DataTable from "components/common/table/DataTable";
import { StatusBadge } from "components/common/StatusBadge";
import PageHeader from "components/common/PageHeader";
import ListViewTypeToggler from "components/common/table/ListViewTypeToggler";
import GridTypeTable from "components/common/GridTypeTable";
import { Images } from "assets/images";
import GridShimmer from "components/common/GridShimmer";

const Branches = () => {
  const { t } = useTranslation();
  const { trash, edit, view, noRoles } = Icons || {};
  const { noImagePlaceHolder } = Images || {};
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { canAction } = usePermission();
  const { canView, canEdit, canDelete, canAdd } = canAction("branches");
  const [viewMode, setViewMode] = useState("list");

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
  };

  const {
    loading,
    isLoading,
    loadingItemId,
    items,
    totalPages,
    currentPage,
    totalCount,
    filters: { keyword, dates, status, orderBy },
  } = useSelector((state) => state.Branches);

  useEffect(() => {
    if (canView) {
      getAllData();
    }
  }, []);

  const getAllData = (page, action) => {
    const { isReset, isResetKeyword } = getActionFlags(action);

    const payload = {
      perPage: 50,
      pageNo: page || 1,
      orderBy: isReset ? -1 : orderBy,
      status: isReset ? -1 : status,
      keyword: isResetKeyword ? "" : keyword || "",
      dateRange: isReset ? {} : getMinMaxDate(dates),
    };
    dispatch(getBranches(payload));
  };

  const handleReset = () => {
    dispatch(resetFilters());
    getAllData(1, { actionType: "RESET" });
  };

  const filtersList = [
    {
      key: "status",
      placeholder: t("Status"),
      type: "singleSelect",
      options: STATUS_OPTIONS,
    },
    {
      key: "orderBy",
      placeholder: t("Order By"),
      type: "singleSelect",
      options: ORDER_BY_OPTIONS,
    },
  ];

  const columns = useMemo(
    () => [
      {
        header: t("Store No"),
        key: "storeNo",
      },
      {
        header: t("Name"),
        key: "name",
        render: (value) => value?.en || "-",
      },
      {
        header: t("Address"),
        key: "address",
        render: (value) => <p style={{ minWidth: 200 }}>{value?.en || "-"}</p>,
      },
      {
        header: t("Traffic Percentage"),
        key: "trafficPercentage",
        render: (value) => `${value}%`,
      },
      {
        header: t("Traffic Status"),
        key: "trafficStatus",
        render: (value) => `${value}`,
      },
      {
        header: t("Wait Time (In Minutes)"),
        key: "waitTimeInMinutes",
        render: (value) => `${value} Minutes`,
      },
      {
        header: t("Status"),
        key: "status",
        render: (value) => <StatusBadge status={value} t={t} />,
      },
    ],
    [t],
  );

  const renderActions = useCallback(
    (item) => (
      <>
        {canEdit && (
          <>
            <Switch
              className="branch_toggle"
              checked={item.status === 1}
              handleOnChange={() => {
                handleStatusUpdate({
                  _id: item._id,
                  status: item.status,
                  dispatch,
                  action: updateBranchStatus,
                });
              }}
              disabled={isLoading && loadingItemId === item._id}
            />
            <img
              src={edit}
              width={20}
              height={20}
              alt={t("edit")}
              onClick={() => navigate(`/stores/edit-store?id=${item._id}`)}
            />
          </>
        )}
        {canView && (
          <Link to={`/stores/store-details?id=${item._id}`}>
            <img src={view} width={20} height={20} alt={t("view")} />
          </Link>
        )}

        {canDelete && (
          <img
            src={trash}
            width={20}
            height={20}
            alt={t("delete")}
            onClick={() => {
              dispatch(
                showDeleteModal({
                  title: t("Delete Store"),
                  message: t("Are you sure you want to delete this Store?"),
                  itemId: item._id,
                  actionType: "BRANCH",
                }),
              );
            }}
          />
        )}
      </>
    ),
    [canEdit, canView, canDelete, isLoading, loadingItemId],
  );

  // Grid Card Header Renderer
  const renderBranchCardHeader = useCallback(
    (branch, t) => (
      <>
        <h3 className="branch-name">{branch.name?.en || branch.name || "—"}</h3>
        <img
          src={
            branch?.imageUrls?.length ? branch.imageUrls[0] : noImagePlaceHolder
          }
          height={180}
          width="100%"
          alt={`${branch?.name?.en || t("Branch")}`}
        />
      </>
    ),
    [],
  );

  // Grid Card Body Renderer
  const renderBranchCardBody = useCallback(
    (branch, t) => (
      <div className="stats-grid">
        <div className="stat-item">
          <span className="label">{t("Traffic Percentage")}</span>
          <span className="value">{branch.trafficPercentage ?? 0}%</span>
        </div>
        <div className="stat-item">
          <span className="label">{t("Wait time")}</span>
          <span className="value">
            {branch.waitTimeInMinutes ?? 0} {t("Min.")}
          </span>
        </div>
        <div className="stat-item">
          <span className="label">{t("Traffic status")}</span>
          <span className="value">{branch.trafficStatus || "—"}</span>
        </div>
      </div>
    ),
    [],
  );

  // Grid Card Footer Renderer
  const renderBranchCardFooter = useCallback(
    (branch, t) => (
      <div className="grid-actions">
        <div className="left-icons">
          {canView && (
            <Link to={`/stores/store-details?id=${branch._id}`}>
              <img src={view} width={20} height={20} alt={t("view")} />
            </Link>
          )}
          {canEdit && (
            <img
              src={edit}
              width={20}
              height={20}
              alt={t("edit")}
              onClick={() => navigate(`/stores/edit-store?id=${branch._id}`)}
            />
          )}
          {canDelete && (
            <img
              src={trash}
              width={20}
              height={20}
              alt={t("delete")}
              onClick={() =>
                dispatch(
                  showDeleteModal({
                    title: t("Delete Store"),
                    message: t("Are you sure you want to delete this Store?"),
                    itemId: branch._id,
                    actionType: "BRANCH",
                  }),
                )
              }
            />
          )}
        </div>

        {canEdit && (
          <Switch
            className="branch_toggle"
            checked={branch.status === 1}
            handleOnChange={() =>
              handleStatusUpdate({
                _id: branch._id,
                status: branch.status,
                dispatch,
                action: updateBranchStatus,
              })
            }
            disabled={isLoading && loadingItemId === branch._id}
          />
        )}
      </div>
    ),
    [canEdit, canView, canDelete, isLoading, loadingItemId],
  );

  const viewToggle = (
    <ListViewTypeToggler
      viewMode={viewMode}
      onChange={(value) => handleViewModeChange(value)}
    />
  );

  if (!canView) return null;

  return (
    <div className="page-content branches_page">
      <PageHeader title={t("Stores")} />
      <FilterBar
        keyword={keyword}
        setKeyword={(value) => dispatch(setFilter({ key: "keyword", value }))}
        keywordPlaceholder={t("Search by Name")}
        filters={filtersList}
        selectedFilters={{ keyword, status, orderBy }}
        onFilterUpdate={(key, value) => dispatch(setFilter({ key, value }))}
        dates={dates}
        setDates={(value) => dispatch(setFilter({ key: "dates", value }))}
        onSearch={getAllData}
        onReset={handleReset}
        isBtn={canAdd}
        btnText={t("Add Store")}
        onBtnClick={() => navigate("/stores/add-store")}
        rightActions={viewToggle}
      />
      {viewMode === "list"
        ? loading && <TableShimmer rows={10} columns={4} />
        : loading && <GridShimmer rows={10} columns={4} />}

      {!loading && items?.length === 0 && (
        <NoDataFound title={t("No stores found")} icon={noRoles} />
      )}

      {!loading && items?.length > 0 && (
        <>
          {viewMode === "grid" ? (
            <GridTypeTable
              data={items}
              renderCardHeader={renderBranchCardHeader}
              renderCardBody={renderBranchCardBody}
              renderCardFooter={renderBranchCardFooter}
              emptyMessage={t("No branches found")}
              gridColumns={{ lg: 3, md: 3, sm: 6, xs: 12 }}
              t={t}
            />
          ) : (
            <DataTable
              columns={columns}
              data={items}
              renderActions={renderActions}
              t={t}
            />
          )}

          <Paginations
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={getAllData}
          />
        </>
      )}
    </div>
  );
};

export default Branches;
